'use client';

/**
 * Per-page browser tab titles.
 *
 * Every page in this app is a client component, so none of them can export
 * Next's `metadata` — which is why all ten open UZO tabs read the same
 * "Workflow Builder - Multi-AI Agentic Platform" and you had to click each one
 * to find the right one. Deriving the title from the pathname in a single
 * always-mounted component fixes the whole app at once, and keeps working for
 * routes added later (the fallback is "UZO").
 */

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

/**
 * FIRST MATCH WINS — the lookup below is `.find(([p]) => pathname.startsWith(p))`,
 * not a longest-prefix search. It only behaves like one because this list is
 * ordered longest-first, so a new entry MUST go above any prefix of itself
 * (in particular above '/monitor'), or it can never match.
 */
const ROUTE_TITLES: Array<[string, string]> = [
  ['/monitor/agents', 'Agent observability'],
  ['/monitor/workflows', 'Workflow observability'],
  // Both app surfaces get the same title: /monitor/crafts2 is the linked one,
  // /monitor/apps is unlinked but still reachable.
  ['/monitor/crafts2', 'App observability'],
  ['/monitor/apps', 'App observability'],
  ['/monitor/gateways', 'Gateway observability'],
  ['/monitor/budgets', 'Budgets'],
  ['/monitor/kb', 'Knowledge observability'],
  ['/monitor/rag', 'RAG observability'],
  ['/monitor', 'Observability'],
  ['/workflow-builder', 'Workflow editor'],
  ['/workflows', 'Workflows'],
  ['/knowledgebases-classic', 'Knowledgebases (classic)'],
  ['/knowledgebases', 'Knowledgebases'],
  ['/mcp-registry', 'MCP registry'],
  ['/connectors', 'Connectors'],
  ['/evaluations', 'Evaluations'],
  ['/approvals', 'Approvals'],
  ['/schedules', 'Schedules'],
  ['/workspaces', 'Workspaces'],
  ['/projects', 'Projects'],
  ['/agents', 'Agents'],
  ['/craft', 'Craft'],
  ['/chat', 'Chat'],
  ['/settings', 'Settings'],
  ['/dashboard', 'Home'],
  ['/docs', 'Learning center'],
  ['/login', 'Sign in'],
];

export function DocumentTitle() {
  const pathname = usePathname() || '';

  useEffect(() => {
    const match = ROUTE_TITLES.find(([prefix]) => pathname.startsWith(prefix));
    document.title = match ? `${match[1]} · UZO` : 'UZO';
  }, [pathname]);

  return null;
}

export default DocumentTitle;
